import { Box, Button, keyframes, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

import { INTERACTION } from "../login-utils/flowInteractions";
import { primaryButtonSx } from "./LoginChrome";
import {
	EXPIRY_SECONDS,
	formatPollingTime,
	getLinkId,
	getPollingEmail,
	POLL_INTERVAL_MS
} from "../login-utils/pollingUtils";
import type { ByosScreenProps } from "../login-utils/types";

const numberPulse = keyframes`
	0%, 100% { transform: scale(1);    opacity: 0.96; }
	50%      { transform: scale(1.03); opacity: 1.00; }
`;

const ringPulse = keyframes`
	0%   { box-shadow: 0 0 0 0   rgba(2, 213, 171, 0.35); }
	70%  { box-shadow: 0 0 0 18px rgba(2, 213, 171, 0.00); }
	100% { box-shadow: 0 0 0 0   rgba(2, 213, 171, 0.00); }
`;

export default function Polling({
	onNext,
	form,
	context,
	errorText,
	isSubmitting = false
}: ByosScreenProps) {
	const email = useMemo(() => getPollingEmail(form, context), [form, context]);
	const linkId = useMemo(() => getLinkId(context), [context]);
	const [secondsLeft, setSecondsLeft] = useState(EXPIRY_SECONDS);
	const [resentJustNow, setResentJustNow] = useState(false);

	useEffect(() => {
		if (secondsLeft <= 0) return;
		const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
		return () => clearInterval(id);
	}, [secondsLeft]);

	useEffect(() => {
		if (!resentJustNow) return;
		const id = setTimeout(() => setResentJustNow(false), 2500);
		return () => clearTimeout(id);
	}, [resentJustNow]);

	const progress = Math.max(0, Math.min(100, ((EXPIRY_SECONDS - secondsLeft) / EXPIRY_SECONDS) * 100));
	const expired = secondsLeft === 0;

	const onNextRef = useRef(onNext);
	const formRef = useRef(form);
	onNextRef.current = onNext;
	formRef.current = form;

	useEffect(() => {
		if (expired) return;

		let cancelled = false;
		let inFlight = false;

		const poll = async () => {
			if (cancelled || inFlight) return;
			inFlight = true;
			try {
				await onNextRef.current(INTERACTION.POLL, formRef.current);
			} catch {
				// Still waiting for the user to open the enchanted link.
			} finally {
				inFlight = false;
			}
		};

		void poll();
		const id = setInterval(() => void poll(), POLL_INTERVAL_MS);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, [expired]);

	const handleResend = async () => {
		setSecondsLeft(EXPIRY_SECONDS);
		setResentJustNow(true);
		await onNext(INTERACTION.RESEND_LINK, form);
	};

	return (
		<Stack sx={{ gap: 2.5, alignItems: "center", textAlign: "center", py: 1 }}>
			<Typography
				sx={{
					fontSize: 22,
					fontWeight: 600,
					color: "rgba(13, 31, 48, 0.92)",
					letterSpacing: "-0.01em"
				}}
			>
				Check your email
			</Typography>

			<Typography sx={{ fontSize: 14, color: "rgba(13, 31, 48, 0.70)", lineHeight: 1.55, maxWidth: 320 }}>
				We sent a sign-in link to{" "}
				<Box component="span" sx={{ color: "rgba(13, 31, 48, 0.92)", fontWeight: 600 }}>
					{email}
				</Box>
				. Open it and click the number below to finish signing in.
			</Typography>

			<Box
				sx={{
					mt: 1,
					width: 96,
					height: 96,
					borderRadius: "50%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "rgba(2, 213, 171, 0.06)",
					animation: `${ringPulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`
				}}
			>
				<Typography
					sx={{
						fontSize: 40,
						fontWeight: 700,
						color: "rgba(13, 31, 48, 0.92)",
						letterSpacing: "-0.02em",
						animation: `${numberPulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
						fontVariantNumeric: "tabular-nums"
					}}
				>
					{linkId ?? "—"}
				</Typography>
			</Box>

			<Stack sx={{ width: "100%", gap: 0.75, mt: 1 }}>
				<Box
					sx={{
						height: 4,
						width: "100%",
						borderRadius: 2,
						background: "rgba(13, 31, 48, 0.06)",
						overflow: "hidden"
					}}
				>
					<Box
						sx={{
							height: "100%",
							width: `${progress}%`,
							background: "linear-gradient(90deg, #02D5AB 0%, #0A7FA8 100%)",
							transition: "width 0.5s linear",
							borderRadius: 2
						}}
					/>
				</Box>
				<Typography
					sx={{
						fontSize: 12,
						color: expired ? "#C04A4A" : "rgba(13, 31, 48, 0.55)",
						fontVariantNumeric: "tabular-nums"
					}}
				>
					{expired
						? "Link expired. Send a new one to keep going."
						: `Link expires in ${formatPollingTime(secondsLeft)}`}
				</Typography>
			</Stack>

			{errorText && (
				<Typography sx={{ fontSize: 13, color: "#C04A4A" }}>
					{errorText}
				</Typography>
			)}

			{resentJustNow && (
				<Typography
					sx={{
						fontSize: 13,
						color: "var(--coapp-text-interactive, #057A6D)",
						fontWeight: 500,
						mt: -0.5,
						animation: "fadeIn 0.25s ease-out",
						"@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } }
					}}
				>
					Sent — check your inbox.
				</Typography>
			)}

			<Stack sx={{ width: "100%", gap: 1, mt: 1 }}>
				<Button
					onClick={() => void handleResend()}
					sx={primaryButtonSx}
					fullWidth
					disabled={isSubmitting}
				>
					Send another link
				</Button>
			</Stack>
		</Stack>
	);
}
